import { userService } from '../services/userService'
import type { Contact, PreUser, User } from '../types/user'

export interface DisplayMember {
  name: string
  email?: string
  type: 'user' | 'preuser' | 'contact'
  status?: string
}

/**
 * Get display name with company information for a user ID
 * Returns "First Last (Company Name)" or "First Last" if no company
 */
export async function getDisplayNameByUserId(userId: number): Promise<string> {
  try {
    // Fetch user data
    const user = await userService.getUserById(userId)

    // Fetch company data
    const companyInfo = await userService.getUserCompanyPublic(userId)

    // Create display name
    const fullName = `${user.first_name} ${user.last_name}`.trim() || user.email
    const companyName = companyInfo?.name

    return companyName ? `${fullName} (${companyName})` : fullName
  } catch (error) {
    console.error(`Failed to get display name for user ${userId}:`, error)
    // Fallback to just the user ID if everything fails
    return `User ${userId}`
  }
}

/**
 * Get display name from User, PreUser, or Contact
 */
export function getDisplayName(
  user?: User,
  preuser?: PreUser,
  contact?: Contact,
): string {
  if (user) {
    const fullName = `${user.first_name} ${user.last_name}`.trim() || user.email
    const companyName = user.company?.name
    return companyName ? `${fullName} (${companyName})` : fullName
  }
  if (preuser) {
    return preuser.name || preuser.email
  }
  if (contact?.contact_object) {
    return contact.contact_object.name || contact.contact_object.email
  }
  return '-'
}

/**
 * Get all relationship members (users + preusers + contacts) for display
 */
export function getAllRelationshipMembers(
  users: User[] = [],
  preusers: PreUser[] = [],
  contacts: Contact[] = [],
): DisplayMember[] {
  const members: DisplayMember[] = []

  // Add Users (signed up)
  for (const user of users) {
    members.push({
      name: getDisplayName(user),
      email: user.email,
      type: 'user',
      status: '✅ Active',
    })
  }

  // Add PreUsers (invited)
  for (const preuser of preusers) {
    members.push({
      name: getDisplayName(undefined, preuser),
      email: preuser.email,
      type: 'preuser',
      status: '📧 Invited',
    })
  }

  // Add Contacts (legacy fallback)
  for (const contact of contacts) {
    members.push({
      name: getDisplayName(undefined, undefined, contact),
      email: contact.contact_object?.email,
      type: 'contact',
      status: '📇 Contact',
    })
  }

  return members
}

/**
 * Get broker names for project display
 */
export function getBrokerNames(
  brokers: User[] = [],
  brokerPreusers: PreUser[] = [],
  brokerContacts: Contact[] = [],
): string {
  const members = getAllRelationshipMembers(
    brokers,
    brokerPreusers,
    brokerContacts,
  )

  if (members.length === 0) return '-'

  return members.map((member) => member.name).join(', ')
}

/**
 * Get contractor names for project display
 */
export function getContractorNames(
  contractors: User[] = [],
  contractorPreusers: PreUser[] = [],
  contractorContacts: Contact[] = [],
): string {
  const members = getAllRelationshipMembers(
    contractors,
    contractorPreusers,
    contractorContacts,
  )

  if (members.length === 0) return '-'

  return members.map((member) => member.name).join(', ')
}

/**
 * Get site supervisor names for project display
 */
export function getSiteSupervisorNames(siteSupervisors: User[] = []): string {
  if (siteSupervisors.length === 0) return '-'

  return siteSupervisors
    .map((user) => `${user.first_name} ${user.last_name}`.trim())
    .join(', ')
}

/**
 * Get assigned person name for workorder/tag display
 */
export function getAssignedPersonName(
  user?: User,
  preuser?: PreUser,
  contact?: Contact,
): string {
  if (user) return getDisplayName(user)
  if (preuser) return getDisplayName(undefined, preuser)
  if (contact) return getDisplayName(undefined, undefined, contact)
  return '-'
}
